import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import {
  DeleteIssueAction,
  FilterCtfAdminAction,
  GetAllIssuesAction,
  GetCtfAdminAction,
  GetIssuesByEnigmeIdAction,
  GetSelectedCtfAdminAction,
} from "./ctf-admin.actions";
import { tap } from "rxjs";
import { EnigmeAdminModel } from "../models/enigmeAdmin.model";
import { AdminService } from "../services/admin.service";
import { IssueAdminDTO } from "../models/issueAdminDTO.model";
import { TranslateService } from "@ngx-translate/core";
import { getEnigmeName } from "../utils/enigme-admin.utils";

export interface CtfAdminStateModel {
  selectedEnigme: EnigmeAdminModel | null;
  allEnigmes: EnigmeAdminModel[];
  filteredEnigmes: EnigmeAdminModel[];
  allIssues: IssueAdminDTO[];
  enigmeIssues: IssueAdminDTO[];
  issuesCounts: { [key: number]: number };
}

@State<CtfAdminStateModel>({
  name: "CtfAdminStore",
  defaults: {
    selectedEnigme: null,
    allEnigmes: [],
    filteredEnigmes: [],
    allIssues: [],
    enigmeIssues: [],
    issuesCounts: {},
  },
})
@Injectable()
export class CtfAdminState {
  constructor(
    private service: AdminService,
    private translate: TranslateService,
  ) {}

  @Selector()
  static selectCtfs(state: CtfAdminStateModel) {
    return state.filteredEnigmes;
  }

  @Selector()
  static selectAllCtfs(state: CtfAdminStateModel) {
    return state.allEnigmes;
  }

  @Selector()
  static selectSelectedCtf(state: CtfAdminStateModel) {
    return state.selectedEnigme;
  }

  @Selector()
  static selectAllIssues(state: CtfAdminStateModel) {
    return state.allIssues;
  }

  @Selector()
  static selectEnigmeIssues(state: CtfAdminStateModel) {
    return state.enigmeIssues;
  }

  @Selector()
  static selectIssuesCounts(state: CtfAdminStateModel) {
    return state.issuesCounts;
  }

  /**
   * State action for all enigmes
   * @param ctx StateContext<CtfStateModel>
   * @returns all enigme and enigme filtered and distincts tags
   */
  @Action(GetCtfAdminAction)
  getCtfAdminAction(ctx: StateContext<CtfAdminStateModel>) {
    return this.service.getAllEnigmes().pipe(
      tap((enigmeFetched) => {
        ctx.patchState({
          allEnigmes: enigmeFetched?.enigmeList,
          filteredEnigmes: enigmeFetched?.enigmeList,
        });
      }),
    );
  }

  /**
   * State action for selected enigme
   * @param ctx StateContext<CtfStateModel>
   * @param id the enigme id
   * @returns the related enigme
   */
  @Action(GetSelectedCtfAdminAction)
  getSelectedCtfAdminAction(
    ctx: StateContext<CtfAdminStateModel>,
    action: GetSelectedCtfAdminAction,
  ) {
    return this.service.getEnigmeById(action.id).pipe(
      tap((enigmeFetched) => {
        ctx.patchState({ selectedEnigme: enigmeFetched });
      }),
    );
  }

  @Action(FilterCtfAdminAction)
  filterCtfAfminAction(
    ctx: StateContext<CtfAdminStateModel>,
    action: FilterCtfAdminAction,
  ) {
    const allEnigmes = ctx.getState().allEnigmes;
    ctx.patchState({
      filteredEnigmes: allEnigmes.filter(
        (e) =>
          (!action.filter.type || e.tags.includes(action.filter.type)) &&
          (action.filter.difficulty === null ||
            action.filter.difficulty === undefined ||
            e.difficulty === +action.filter.difficulty) &&
          (!action.filter.search ||
            getEnigmeName(e, this.translate.getCurrentLang())
              .toLowerCase()
              .includes(action.filter.search.toLowerCase())),
      ),
    });
  }

  /**
   * State action for all issues
   * @param ctx StateContext<CtfStateModel>
   * @returns all issues and updates issues counts by enigme
   */
  @Action(GetAllIssuesAction)
  getAllIssuesAction(ctx: StateContext<CtfAdminStateModel>) {
    return this.service.getAllIssues().pipe(
      tap((issuesFetched) => {
        const issuesCounts: { [key: number]: number } = {};

        // Count issues per enigme
        if (issuesFetched && issuesFetched.length > 0) {
          issuesFetched.forEach((issue) => {
            if (!issuesCounts[issue.idEnigme]) {
              issuesCounts[issue.idEnigme] = 0;
            }
            issuesCounts[issue.idEnigme]++;
          });
        }

        ctx.patchState({
          allIssues: issuesFetched || [],
          issuesCounts: issuesCounts,
        });
      }),
    );
  }

  /**
   * State action for issues by enigme id
   * @param ctx StateContext<CtfStateModel>
   * @param enigmeId the enigme id
   * @returns issues for a specific enigme
   */
  @Action(GetIssuesByEnigmeIdAction)
  getIssuesByEnigmeIdAction(
    ctx: StateContext<CtfAdminStateModel>,
    action: GetIssuesByEnigmeIdAction,
  ) {
    return this.service.getIssuesByEnigmeId(action.enigmeId).pipe(
      tap((issuesFetched) => {
        ctx.patchState({ enigmeIssues: issuesFetched || [] });
      }),
    );
  }

  /**
   * State action for deleting an issue
   * @param ctx StateContext<CtfStateModel>
   * @param issueId the issue id to delete
   * @param enigmeId the enigme id related to the issue
   * @returns the deleted issue
   */
  @Action(DeleteIssueAction)
  deleteIssueAction(
    ctx: StateContext<CtfAdminStateModel>,
    action: DeleteIssueAction,
  ) {
    return this.service.deleteIssue(action.issueId).pipe(
      tap((deletedIssue) => {
        // Mettre à jour la liste des issues de l'énigme
        const currentState = ctx.getState();
        const updatedIssues = currentState.enigmeIssues.filter(
          (issue) => issue.id !== action.issueId,
        );

        // Mettre à jour le compteur d'issues pour cette énigme
        const updatedCounts = { ...currentState.issuesCounts };
        if (
          updatedCounts[action.enigmeId] &&
          updatedCounts[action.enigmeId] > 0
        ) {
          updatedCounts[action.enigmeId]--;
        }

        // Mettre à jour toutes les issues
        const updatedAllIssues = currentState.allIssues.filter(
          (issue) => issue.id !== action.issueId,
        );

        ctx.patchState({
          enigmeIssues: updatedIssues,
          allIssues: updatedAllIssues,
          issuesCounts: updatedCounts,
        });
      }),
    );
  }
}
